import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import * as Mui from "@mui/material";
import { useContext, useMemo, useState } from "react";
import { Route, Routes, useNavigate, useParams, useLocation } from "react-router-dom";
import SectionHeader from "../../../../util/component/SectionHeader";
import { ContextOperation } from "../../context/OperationContext";
import useOperationTranslation from "../../hooks/useOperationTranslation";
import OperationTypes from "../../types";
import EnumChangeName from "../common/EnumChangeName";
import CarSegment from "./DetailsPage/CarSegment";
import Drivers from "./DetailsPage/Drivers";
import Tender from "./DetailsPage/Tender";
import TransferRequest from "./DetailsPage/TransferRequest";
import Vehicles from "./DetailsPage/Vehicles";
import { TransferContext } from "./TransferContext";
import TransferReasonService from "./TransferReasonService";
import TransferRequestTabs from "./TransferRequestTabs";
import { TransferReasonTypes } from "./Types";

const TransferRequestDetails = () => {
  const navigate = useNavigate();
  const t = useOperationTranslation();
  const context = useContext(TransferContext);
  const { tenant } = useContext(ContextOperation);
  const { id } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const tenantCallout = queryParams.get('tenant');

  const { locationType } = EnumChangeName();

  const [transferRequest, setTransferRequest] =
    useState<TransferReasonTypes.TransferRequestDetails>();

  const [carSegment, setCarSegment] =
    useState<TransferReasonTypes.CarSegmentDetails[]>();
  const [drivers, setDrivers] = useState<OperationTypes.Drivers[]>();
  const [vehicles, setVehicles] =
    useState<OperationTypes.DeliveryNotesInfo[]>();
  const [tenderWinner, setTenderWinner] = useState<
    TransferReasonTypes.TenderDetails | undefined
  >();

  let status = (params: string) => {
    switch (params) {
      case "REJECTED":
        return t("transferReason.autocomplete.rejected");

      case "APPROVED":
        return t("transferReason.autocomplete.approved");

      case "TO_BE_APPROVED":
        return t("transferReason.autocomplete.toBeApproved");

      case "CREATED":
        return t("transferReason.autocomplete.created");

      case "APPROVED_WITH_DDT":
        return t("transferReason.autocomplete.approvedWithDdt");
      case "TRANSFER_COMPLETED":
        return t("transferReason.autocomplete.transferCompleted");
      case "DUPLICATE_CANCELLED_BY_SYSTEM":
        return t("transferReason.autocomplete.duplicateCancelledBySystem");
      default:
        return "";
    }
  };



  let transferType = (params: string) => {
    switch (params) {
      case "DRIVER":
        return t("transferReason.autocomplete.driver");
      case "CAR_TRANSPORTER":
        return t("transferReason.autocomplete.carTransporter");
      default:
        return "";
    }
  };

  useMemo(() => {
    if (!!id)
      TransferReasonService.get
        .transferRequestDetails(!!tenantCallout ? tenantCallout : tenant, id)
        .then((response) => {
          //console.log("Transfer Request Details Response: ", response);
          let reasonCode = context.reason.find(
            (element: any) => element.value === response.reasonCode
          );

          if (response.startStationId) {
            response.startStation = `${response.startStationDescription} (${locationType(response.startStationType)})`;
          }
          if (response.expectedEndStationId) {
            response.expectedEndLocation = `${response.expectedEndStationDescription} (${locationType(response.expectedEndStationType)})`;
          }
          if (response.endStationId) {
            response.endLocation = `${response.endStationDescription} (${locationType(response.endStationType)})`;
          }
          response.deliveryNoteNumber = !!response?.deliveryNote.number
            ? response.deliveryNote.number
            : "";
          response.status = status(response.status);
          response.requestNumber = response.number;
          response.reasonCode = reasonCode?.label;
          response.transferType = transferType(response.transferType);
          setTransferRequest(response);

          setCarSegment(
            !!response.carSegmentsInfo ? response.carSegmentsInfo : []
          );

          if (response?.deliveryNote !== undefined) {
            setDrivers(response?.deliveryNote?.drivers.map((elem: any) => {
                elem.driverStationDescription = response.driverStationDescription;
                return elem;
            }));
            setVehicles(response?.deliveryNote?.deliveryNotesInfo);
          }

          setTenderWinner(
            !!response.tenderWinner ? response.tenderWinner : undefined
          );
        });
  }, [context, id, tenant, tenantCallout]);

  return (
    <>
        <SectionHeader
            title={t("transferReason.titleFirst") + " " + t("transferReason.titleSecond")}
            subtitle={
              <Mui.Grid container spacing={2}>
                <Mui.Grid item xs="auto" display="flex" gap={2} alignItems="center">
                  <Mui.Typography variant="h6">
                    {t("transferReason.title.details.numeroRichiesta")}
                  </Mui.Typography>
                  <Mui.Typography>{transferRequest?.requestNumber}</Mui.Typography>
                </Mui.Grid>
                
                <Mui.Grid item xs="auto" display="flex" gap={2} alignItems="center">
                  <Mui.Typography variant="h6">
                    {t("transferReason.title.details.tipoTrasporto")}
                  </Mui.Typography>
                  <Mui.Typography>{transferRequest?.transferType}</Mui.Typography>
                </Mui.Grid>

                <Mui.Grid item xs="auto" display="flex" gap={2} alignItems="center">
                  <Mui.Typography variant="h6">
                    {t("transferReason.title.details.stato")}
                  </Mui.Typography>
                  <Mui.Typography>{transferRequest?.status}</Mui.Typography>
                </Mui.Grid>
              </Mui.Grid>
            }
        />
      <TransferRequestTabs transferRequest={transferRequest?.transferType} />
      <Mui.Box m={2} p={2} borderColor={"red"} border={1} borderRadius={1}>
        <Mui.Box pb={2}>
          <Mui.Stack direction="row" alignItems="center" gap={1}>
            <Mui.IconButton
              onClick={() => navigate("/operation/transfer/transferRequests")}
              color={"primary"}
            >
              <ArrowBackIcon fontSize="small" />
            </Mui.IconButton>
            <Mui.Typography variant="h5">
              {t("transferReason.details.back")}
            </Mui.Typography>
          </Mui.Stack>
        </Mui.Box>
        <Mui.Box>
          <Routes>
            <Route
              path="/"
              element={<TransferRequest transferRequest={transferRequest} />}
            />
            <Route
              path="/carsegment"
              element={<CarSegment carSegment={carSegment} />}
            />
            <Route
              path="/vehicles"
              element={<Vehicles vehicles={vehicles} />}
            />
            <Route
              path="/tenders"
              element={<Tender tenderWinner={tenderWinner} />}
            />
            <Route path="/drivers" element={<Drivers drivers={drivers} />} />
          </Routes>
        </Mui.Box>
      </Mui.Box>
    </>
  );
};

export default TransferRequestDetails;
